import TYPES from '@/types';
import Vue from 'vue';
import { currencyFormat } from '@/vue-app/utils/currency';
import { parseCurrencyToNumber } from '@/vue-app/utils/parse-currency-to-number';

// Domain
import {
  LinkGoalsWealthStateManager,
} from '@/modules/flagship/investor-goal/link-sowos-wealth/domain/state/link-goals-wealth-state-manager';
import {
  StateManager as ContributionsStateManager,
} from '@/modules/my-investment/allianz/recurring-contributions/domain/state/state-manager';
import Inject from '@/modules/shared/domain/di/inject';
import Translator from '@/modules/shared/domain/i18n/translator';
import { Values } from '@/modules/shared/domain/i18n/types';

export default class StepDistributeBalanceWealthViewModel {
  @Inject(TYPES.LINK_GOALS_WEALTH_STATE_MANAGER)
  readonly link_goals_wealth_state_manager!: LinkGoalsWealthStateManager;

  @Inject(TYPES.ALLIANZ_RECURRING_CONTRIBUTIONS_STATE_MANAGER)
  private readonly contributions_state_manager!:
    ContributionsStateManager;

  @Inject(TYPES.I18N)
  readonly translator!: Translator;

  private readonly view!: Vue;

  readonly i18n_namespace = 'components.flagship.flagship-link-goals.step-distribute-balance-wealth';

  link_goals_wealth_state = this.link_goals_wealth_state_manager.state;

  contributions_state = this.contributions_state_manager.state;

  balance = 0;

  is_disabled = false;

  free_balance = 0;

  has_active_goals = false;

  public constructor(view: Vue) {
    this.view = view;
  }

  initialize = async () => {
    this.link_goals_wealth_state.is_loading = true;
    await this.loadUnallocatedBalance();
    await this.getAssociatedProduct();
    this.link_goals_wealth_state.is_loading = false;
  }

  translate = (message: string, values?: Values) => this.translator.translate(`${this.i18n_namespace}.${message}`, values);

  get balance_formatted() {
    return currencyFormat(this.balance);
  }

  get free_balance_formatted() {
    return currencyFormat(this.free_balance);
  }

  getAssociatedProduct = async () => {
    this.link_goals_wealth_state.selected_goals.forEach((selected_goal) => {
      this.free_balance -= selected_goal.active_goal_amount;
    });
  };

  changeActiveGoalAmount = (index: number) => {
    const assigned_amount = this.link_goals_wealth_state.selected_goals[index]
      .active_goal_amount_field;
    let active_goal_amount = assigned_amount
      ? parseCurrencyToNumber(
        this.link_goals_wealth_state.selected_goals[index].active_goal_amount_field,
      )
      : 0;
    active_goal_amount = (active_goal_amount < 0) ? 0 : active_goal_amount;
    this.link_goals_wealth_state.selected_goals[index].active_goal_amount = active_goal_amount
    < this.balance ? active_goal_amount : this.balance;
    this.link_goals_wealth_state.selected_goals[index].active_goal_amount_field = active_goal_amount
    < this.balance
      ? currencyFormat(active_goal_amount) : currencyFormat(this.balance);
    this.validateBalanceAndAmountSlider();
  }

  validateBalanceAndAmountSlider = () => {
    const amount_sliders = this.link_goals_wealth_state.selected_goals.map((obj) => (
      Number(parseFloat(obj.active_goal_amount_field.replaceAll(/[^\d.-]/g, '')).toFixed(2))
    ));
    const total_amount_sliders = amount_sliders.reduce(
      (total, currentValue) => total + currentValue, 0,
    );
    if (total_amount_sliders > this.balance) {
      this.is_disabled = true;
      this.free_balance = total_amount_sliders - this.balance;
    } else {
      this.is_disabled = false;
      this.free_balance = this.balance - total_amount_sliders;
    }
  }

  loadUnallocatedBalance = async () => {
    this.balance = this.link_goals_wealth_state.unassigned_balance;
    this.free_balance = this.balance;
  }

  sendInvestorGoals = () => {
    this.link_goals_wealth_state.selected_goals.forEach((selected_goal, index) => {
      this.link_goals_wealth_state.selected_goals[index].active_goal_amount = parseCurrencyToNumber(
        selected_goal.active_goal_amount_field,
      );
    });
    if (this.contributions_state.item.total_contribution >= 100000) {
      this.link_goals_wealth_state.contribution_limit_reached = true;
      this.view.$emit('linkGoals');
    } else {
      this.view.$emit('nextStep');
    }
  }

  goToPrevStep = () => {
    this.view.$emit('prevStep');
  }
}
